// event.component.ts
import { Component, Inject, OnInit, PLATFORM_ID, OnDestroy } from '@angular/core';
import { ChildrenSelectorComponent, Student } from '../../shared/children-selector/children-selector.component';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { EventService, Event, Reservation } from '../../services/event.service';
import { Subject, takeUntil } from 'rxjs';
import { TranslatePipe } from '@ngx-translate/core';
import { PageTitleComponent } from '../../shared/page-title/page-title.component';
import { AlertService } from '../../services/alert.service';
import { LoaderComponent } from "../../shared/loader/loader.component";
import { HttpClient } from '@angular/common/http';
import { NotificationService } from '../../services/notification.service';

interface SelectableStudent extends Student {
  selected: boolean;
  name: string; // pour le template
}

@Component({
  selector: 'app-event',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslatePipe, ChildrenSelectorComponent, PageTitleComponent, LoaderComponent],
  templateUrl: './event.component.html',
  styleUrls: ['./event.component.css']
})
export class EventComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>(); // Pour unsubscribe automatiquement
selectedStudentId: number | null = null;

  reservations: Reservation[] = [];
  events: Event[] = [];
  activeTab: 'events' | 'reservations' | 'book' = 'events';
  selectedEvent: Event | null = null;

  children: SelectableStudent[] = [];
  selectedStudent: SelectableStudent | null = null;
loading = false; // ← loader activé/désactivé

  parentId: number | null = null; 
  userId: number | null = null; 
  // Pagination réservations
  reservationsPageSize = 1;
  reservationsCurrentPage = 1;

  get reservationsTotalPages(): number {
    return Math.max(1, Math.ceil(this.reservations.length / this.reservationsPageSize));
  }

  get paginatedReservations(): Reservation[] {
    const start = (this.reservationsCurrentPage - 1) * this.reservationsPageSize;
    const end = start + this.reservationsPageSize;
    return this.reservations.slice(start, end);
  }

  goToReservationsPage(page: number) {
    if (page >= 1 && page <= this.reservationsTotalPages) {
      this.reservationsCurrentPage = page;
    }
  }

  previousReservationsPage() {
    if (this.reservationsCurrentPage > 1) this.reservationsCurrentPage -= 1;
  }

  nextReservationsPage() {
    if (this.reservationsCurrentPage < this.reservationsTotalPages) this.reservationsCurrentPage += 1;
  }

  getVisibleReservationPages(): (number | string)[] {
    const total = this.reservationsTotalPages;
    const current = this.reservationsCurrentPage;
    const pages: (number | string)[] = [];
    if (total <= 7) {
      for (let i = 1; i <= total; i++) pages.push(i);
    } else {
      pages.push(1);
      if (current <= 4) {
        for (let i = 2; i <= 5; i++) pages.push(i);
        pages.push('...');
        pages.push(total);
      } else if (current >= total - 3) {
        pages.push('...');
        for (let i = total - 4; i <= total; i++) pages.push(i);
      } else {
        pages.push('...');
        for (let i = current - 1; i <= current + 1; i++) pages.push(i);
        pages.push('...');
        pages.push(total);
      }
    }
    return pages;
  }

  // Pagination événements
  eventsPageSize = 20;
  eventsCurrentPage = 1;
  eventsTotalPagesFromApi: number = 1;
  eventsTotalCount: number = 0;

  get eventsTotalPages(): number {
    if (this.eventsTotalPagesFromApi) {
      return this.eventsTotalPagesFromApi;
    }
    // Fallback: calculate from totalCount if available
    if (this.eventsTotalCount > 0) {
      return Math.ceil(this.eventsTotalCount / this.eventsPageSize);
    }
    return 1;
  }

  get paginatedEvents(): Event[] {
    // Server-side pagination: return all events since API already paginated
    return this.events;
  }

  goToEventsPage(page: number) {
    if (page >= 1 && page <= this.eventsTotalPages) {
      this.eventsCurrentPage = page;
      this.loadEvents(); // Reload events for new page
    }
  }

  previousEventsPage() {
    if (this.eventsCurrentPage > 1) {
      this.eventsCurrentPage -= 1;
      this.loadEvents(); // Reload events for new page
    }
  }

  nextEventsPage() {
    if (this.eventsCurrentPage < this.eventsTotalPages) {
      this.eventsCurrentPage += 1;
      this.loadEvents(); // Reload events for new page
    }
  }

  getVisibleEventPages(): (number | string)[] {
    const total = this.eventsTotalPages;
    const current = this.eventsCurrentPage;
    const pages: (number | string)[] = [];
    if (total <= 7) {
      for (let i = 1; i <= total; i++) pages.push(i);
    } else {
      pages.push(1);
      if (current <= 4) {
        for (let i = 2; i <= 5; i++) pages.push(i);
        pages.push('...');
        pages.push(total);
      } else if (current >= total - 3) {
        pages.push('...');
        for (let i = total - 4; i <= total; i++) pages.push(i);
      } else {
        pages.push('...');
        for (let i = current - 1; i <= current + 1; i++) pages.push(i);
        pages.push('...');
        pages.push(total);
      }
    }
    return pages;
  }


  constructor(
    private eventService: EventService,
    @Inject(PLATFORM_ID) private platformId: Object,
    private alertService: AlertService,
    private http: HttpClient,
    private notificationService: NotificationService
  ) {}

  ngOnInit(): void {
    // Récupérer userId depuis localStorage
    const storedUserId = localStorage.getItem('userId');
    this.userId = storedUserId ? Number(storedUserId) : null;

    this.loadParentId();
    this.loadChildren();
  }
onStudentSelected(student: Student) {
  const selectable: SelectableStudent = {
    ...student,
    selected: true, // ou false selon le contexte
    name: student.nameStudent
  };

  this.selectedStudent = selectable;
  this.selectedStudentId = selectable.idStudent;

  // Recharger événements et réservations pour cet enfant
  this.loadEvents();
  this.loadReservations();
}


  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // 🔹 Récupérer parentId
  private loadParentId() {
    if (!isPlatformBrowser(this.platformId)) return;

    const userStr = localStorage.getItem('userData');
    if (!userStr) return;

    try {
      const user = JSON.parse(userStr);
      this.parentId = user?.idGuardian ?? user?.id ?? null;
    } catch (err) {
      console.error('Erreur parsing userData', err);
      this.parentId = null;
    }
  }

  // 🔹 Charger enfants depuis localStorage
  loadChildren() {
    if (!isPlatformBrowser(this.platformId) || !this.parentId) return;

    const studentListStr = localStorage.getItem('studentList');
    if (!studentListStr) return;

    const allStudents = JSON.parse(studentListStr) as Student[];

    this.children = allStudents
      .filter(s => s.idGuardian === this.parentId)
      .map(s => ({
        ...s,
        selected: false,
        name: s.nameStudent
      }));

    // Sélection par défaut du premier enfant
    if (this.children.length > 0) {
      this.selectChild(this.children[0]);
    }
  }

  // 🔹 Sélectionner un enfant
  selectChild(child: SelectableStudent) {
    this.children.forEach(c => (c.selected = false));
    child.selected = true;
    this.selectedStudent = child;

    // Recharger événements et réservations pour cet enfant
    this.loadEvents();
    this.loadReservations();
  }

  // 🔹 Vérifier si l'étudiant a déjà réservé un événement
hasReservation(eventId: number): boolean {
  if (!this.selectedStudent) return false;
  return this.reservations.some(r => r.event?.id === eventId && r.id != null);
}


  // 🔹 Vérifier si l'événement est passé
  isEventPast(event: Event): boolean {
    const now = new Date();
    const eventEnd = new Date(event.endDate);
    return eventEnd < now;
  }


loadEvents(): void {
  if (!this.selectedStudent) return;

  const student = this.selectedStudent;
  if (!student.idStudent || !student.batchId || !student.nameOrg) return;

  this.loading = true;

  // ✅ Appel du service avec pagination
  this.eventService.searchStudentEvents(
    student.idStudent,
    student.nameOrg,
    student.batchId,
    this.eventsCurrentPage,
    this.eventsPageSize
  )
  .pipe(takeUntil(this.destroy$))
  .subscribe({
    next: (response) => {
      console.log('Events API Response:', response);
      this.events = response.events || [];
      this.eventsTotalPagesFromApi = response.totalPages;
      this.eventsTotalCount = response.totalCount || 0;
      this.loading = false;

      // ✅ Mise à jour des notifications pour chaque événement reçu
      if (this.events.length && student.idUserStudent) {
        this.events.forEach(event => {
          if (event.id) {
            this.notificationService.updateNotificationStatus({
              idType: event.id,
              idStudent: student.idUserStudent,
              nameType: 'Event'
            }).subscribe({
              next: () => {
                // ✅ Actualiser les compteurs de notifications
                this.notificationService
                  .getNotificationCounts(student.idUserStudent.toString())
                  .subscribe();
              },
              error: err => console.error('❌ Erreur updateNotification (Event):', err)
            });
          }
        });
      }
    },
    error: (err) => {
      console.error('❌ Erreur lors de la récupération des événements :', err);
      this.loading = false;
    }
  });
}




// 🔹 Charger réservations pour l'enfant sélectionné
loadReservations(): void {
  if (!this.selectedStudent) return;

  const student = this.selectedStudent;
  if (!student.idStudent || !student.nameOrg) return;

  this.eventService.listReservationsByStudent(student.nameOrg, student.idStudent)
    .pipe(takeUntil(this.destroy$))
    .subscribe({
      next: (reservations: Reservation[]) => {
        // On filtre et mappe les réservations pour sécuriser l'affichage
        this.reservations = Array.isArray(reservations)
          ? reservations
              .filter(r => r.event != null)
              .map(r => ({
                id: r.id,
                creationDate: r.creationDate,
                description: r.description || r.event?.description || '',
                event: r.event,
                studentId: r.studentId,
                guardianId: r.guardianId
              }))
          : [];
      },
      error: (err) => {
        console.error('Erreur récupération réservations :', err);
        this.reservations = [];
      }
    });
}


  // 🔹 Changer l’onglet
  setTab(tab: 'events' | 'reservations') {
    this.activeTab = tab;
    this.selectedEvent = null;
  }

  // 🔹 Sélectionner un événement
  selectEvent(event: Event) {
    this.selectedEvent = event;
    this.activeTab = 'book';
  }

  // 🔹 Réserver un événement
reserveEvent(event: Event) {
  // Vérifications initiales
  if (!event || !this.selectedStudent || this.parentId == null) {
    this.alertService.showError({
      title: 'Erreur',
      message: 'Veuillez sélectionner un étudiant et un parent.'
    });
    return;
  }

  const child = this.selectedStudent;
  const studentId = child.idStudent;
  const userId = child.idUserStudent;
  const parentId = this.parentId;

  // Vérifier que tous les IDs sont valides
  if (studentId == null || userId == null || parentId == null) {
    this.alertService.showError({
      title: 'Erreur',
      message: 'Informations de l’étudiant ou du parent manquantes.'
    });
    return;
  }

  // Confirmation avant réservation
  this.alertService.showConfirmation({
    title: 'Confirmation',
    message: `Êtes-vous sûr de vouloir réserver l'événement "${event.eventName}" ?`
  }).then(isConfirmed => {
    if (!isConfirmed) return;

    // Appel API pour ajouter la réservation
    this.eventService.addReservation(event.id, studentId, parentId, userId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: string) => {
          this.alertService.showSuccess({
            title: 'Succès',
            message: response || 'Réservation effectuée avec succès'
          });
          this.loadReservations();
          this.loadEvents();
        },
        error: (err) => {
          console.error('Erreur lors de la réservation :', err);
          this.alertService.showError({
            title: 'Erreur',
            message: 'Erreur lors de la réservation. Veuillez réessayer.'
          });
        }
      });
  });
}

  // 🔹 Supprimer une réservation
supprimerReservation(reservation: Reservation) {
  if (!reservation.id) return; // ← nouveau champ 'id'

  this.alertService.showDeleteConfirmation({
    title: 'Confirmation de suppression',
    message: 'Êtes-vous sûr de vouloir supprimer cette réservation ?'
  }).then((isConfirmed) => {
    if (!isConfirmed) return;

    this.eventService.deleteReservation(reservation.id).subscribe({
      next: (response: string) => {
        this.alertService.showSuccess({
          title: 'Succès',
          message: 'Réservation supprimée avec succès'
        });

        // Recharge les réservations et événements
        this.loadReservations();
        this.loadEvents();
      },
      error: (error) => {
        console.error('Erreur suppression réservation :', error);
        this.alertService.showError({
          title: 'Erreur',
          message: 'Erreur lors de la suppression de la réservation'
        });
      }
    });
  });
}

  // 🔹 Ouvrir un PDF
  viewPdf(pdfPath: string | undefined) {
    if (!pdfPath) return;

    const cleanedPath = pdfPath.trim().replace(/^https?:\/\/[^/]+/i, '');
    const normalizedPath = cleanedPath.startsWith('/') ? cleanedPath : `/${cleanedPath}`;
    const url = `https://plateforme.educanet.pro${normalizedPath}`;
    window.open(url, '_blank');
  }
}
